import type { CashflowTargetGranularity } from '@moment/horizon-data-access/proposal'

export enum CashflowFrequency {
  Monthly = 'monthly',
  Bimonthly = 'bimonthly',
  Quarterly = 'quarterly',
  SemiAnnually = 'semiannually',
  Annually = 'annually',
  OneTime = 'one_time',
}

export enum CashflowSource {
  Coupon = 'coupon',
  Principal = 'principal',
  CouponAndPrincipal = 'coupon_and_principal',
}

export const CashflowFrequencyMap = {
  [CashflowFrequency.Monthly]: 1,
  [CashflowFrequency.Bimonthly]: 2,
  [CashflowFrequency.Quarterly]: 3,
  [CashflowFrequency.SemiAnnually]: 6,
  [CashflowFrequency.Annually]: 12,
}

export interface CashflowBucket {
  start_date: string | null
  end_date: string
  amount: number
  start_date_formatted: string
  coupon_amount: number
  principal_amount: number
  dividend_amount: number
  total_amount: number
  source?: CashflowSource
  // Planned slots only: the cash that counts toward this bucket's own source.
  graded_amount?: number
  // Placeholder amount in maximize mode (no target or override)
  isProjection?: boolean
  // An empty month between a spaced target's windows; nothing counts toward it.
  isGap?: boolean
  // A planned bucket the edited row doesn't contribute to, so its bar can't be edited.
  isLocked?: boolean
  // The row (0-based) that owns a locked bucket, for the tooltip's "Part of row N" note.
  lockedByRow?: number
}

export interface CashflowFormState {
  target: string
  cashflowFrequency: CashflowFrequency
  // Window width; null is the whole period and is saved as no granularity.
  granularity: CashflowTargetGranularity | null
  startDate: string | null
  endDate: string
  source: CashflowSource
}

export type ImportResult = {
  sheet_metadata: {
    original_file_name: string
  }

  records: CashflowBucket[]
  error_records: {
    data: CashflowBucket[]
    error: Record<keyof CashflowBucket, unknown[]>
  }[]
}

export interface CashflowTargetRowData {
  id: string
  amount: number
  start_date: string | null
  end_date: string
  sourceName?: string
  cashflowSource?: CashflowSource
  violation?: boolean
}

export interface RecurringCashflowRow {
  id: string
  target: string
  cashflowFrequency: CashflowFrequency
  granularity: CashflowTargetGranularity | null
  startDate: string | null
  endDate: string
  // Overrides on extra rows round-trip untouched; the base row's live in the hook's localOverrides.
  overrides: Record<string, number>
}

export interface RecurringCashflowFormState {
  rows: RecurringCashflowRow[] // rows[0] is always the base row
  source: CashflowSource
}
